import * as React from "react";
import { useFormContext } from "react-hook-form";
import { TASK_STATUS_OPTIONS } from "common/utils/tasks";
import type { TargetObject } from "common-client/utils/customObject";
import {
  ButtonSection,
  ContentSection,
  FormSection,
  InputRow,
  PrimaryButtons,
} from "../../Common/__styles__/Modal";
import { Button } from "../../Common/Buttons/Button";
import {
  Datepicker,
  Select,
  Text,
  Textarea,
  UserSelect,
} from "../../Inputs/react-hook-form";
import { TaskFormData } from "./types";
import { useAssociatedRecordTypeOptions } from "./utils";

type Props = {
  disabled?: boolean;
  submitButtonLabel: string;
  targetObject?: Maybe<TargetObject>;
  disableAssociatedRecordTypeField?: boolean;
  onCancel: () => void;
  onSubmit: (values: TaskFormData) => Promise<void>;
};

export function Form({
  disabled,
  submitButtonLabel,
  targetObject,
  disableAssociatedRecordTypeField,
  onCancel,
  onSubmit,
}: Props) {
  const {
    control,
    formState: { errors, isDirty, isSubmitting },
    register,
    handleSubmit,
  } = useFormContext<TaskFormData>();
  const associatedRecordTypeOptions =
    useAssociatedRecordTypeOptions(targetObject);

  return (
    <>
      <ContentSection>
        <FormSection>
          <InputRow>
            <Text
              {...register("name", {
                required: "Task name is required",
              })}
              error={errors.name?.message}
              label="Task name"
              aria-label="Task name"
              required
              disabled={disabled}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <UserSelect
              control={control}
              name="assignees"
              label="Assignee(s)"
              includeUsers={true}
              includePublicUsers={false}
              disabled={disabled}
              showSelectAll={false}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Select
              control={control}
              name="status"
              label="Status"
              disabled={disabled}
              options={TASK_STATUS_OPTIONS}
              size="medium"
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Datepicker
              control={control}
              name="dueDate"
              label="Due date"
              disabled={disabled}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Textarea
              {...register("description")}
              error={errors.description?.message}
              label="Description"
              disabled={disabled}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Select
              control={control}
              name="targetSubmissionTypeId"
              label="Associated record type"
              options={associatedRecordTypeOptions}
              disabled={disabled || disableAssociatedRecordTypeField}
              size="medium"
              styles={{
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </InputRow>
        </FormSection>
      </ContentSection>
      <ButtonSection>
        <PrimaryButtons>
          <Button
            onPress={onCancel}
            size="medium"
            variant="secondary"
            title="Cancel"
          />
          <Button
            onPress={() => handleSubmit(onSubmit)()}
            isDisabled={disabled || !isDirty}
            isPending={isSubmitting}
            size="medium"
            variant="primary"
            type="button"
            title={submitButtonLabel}
          />
        </PrimaryButtons>
      </ButtonSection>
    </>
  );
}
